import { skipToken } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { trpc } from "lib/trpc"
import { AlertCircle, Download, FileText, Plus } from "lucide-react"
import PptxGenJS from "pptxgenjs"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { toast } from "sonner"
import * as XLSX from "xlsx"
import { ClientHeader } from "@/components/client-header"
import { ClientTabNavigation } from "@/components/client-tab-navigation"
import { DocumentList } from "@/components/documents/document-list"
import { EstateRelationshipTree } from "@/components/estate-relationship-tree"
import { UploadDocumentModal } from "@/components/modals/upload-document-modal"
import { Page } from "@/components/page"
import { RecommendationsSection } from "@/components/recommendations-section"
import { RefineReportsSection } from "@/components/refine-reports-section"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Typography } from "@/components/ui/typography"
import type { documentTypeEnum } from "../../../../../../../../api/db/schema/document"
import { ReportTypes } from "../../../../../../../../api/report-generation/report-types"

type DocumentType = (typeof documentTypeEnum.enumValues)[number]

export const Route = createFileRoute("/_authed/_professional/professional/reports/will/$estateId")({
  component: RouteComponent,
})

function RouteComponent() {
  const { estateId } = Route.useParams()
  const { t } = useTranslation()
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [additionalPrompt, setAdditionalPrompt] = useState("")

  // Last Will & Testament enum value
  const willDocumentType: DocumentType = "LAST_WILL_AND_TESTAMENT"

  // Fetch WILL_REVIEW reports for this estate
  const { data: willReports = [] } = trpc.reports.getOfType.useQuery({
    estateId,
    reportTypes: ["WILL_REVIEW"],
  })
  const { data: willInfographic } = trpc.reports.getWillInfographic.useQuery(
    estateId ? { estateId } : skipToken,
  )
  const latestWillReport = willReports[0] ?? null

  // Generate will report mutation
  const queryUtils = trpc.useUtils()
  const generateWillReportMutation = trpc.reports.generate.useMutation({
    onSuccess: () => {
      toast.success(t("pages.reports.will.generationStarted"), {
        description: t("pages.reports.estate.generateReports.emailNoticeReport"),
        duration: 8000,
      })
      // Invalidate queries to refresh data when report completes
      queryUtils.reports.getOfType.invalidate({ estateId, reportTypes: ["WILL_REVIEW"] })
      queryUtils.reports.getWillInfographic.invalidate({ estateId })
    },
    onError: (error) => {
      toast.error(t("pages.reports.will.generationError", { message: error.message }))
    },
  })

  return (
    <Page
      headerSlot={<ClientHeader estateId={estateId} />}
      tabSlot={<ClientTabNavigation estateId={estateId} />}
    >
      <div className="space-y-6">
        <RecommendationsSection
          title="Last Will & Testament Recommendations"
          headerAction={
            <div className="flex items-center gap-2">
              <DownloadWillDataExcel infographicData={willInfographic} />
              <DownloadWillRecommendationsPowerPoint
                report={latestWillReport}
                infographicData={willInfographic}
              />
            </div>
          }
          refineSection={
            <RefineReportsSection>
              <div className="space-y-4">
                {/* Document List */}
                <DocumentList
                  estateId={estateId}
                  documentType={willDocumentType}
                  onDocumentDeleted={() => {
                    queryUtils.documents.listByType.invalidate()
                  }}
                />

                {/* Add Document Button */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-auto"
                  onClick={() => setUploadModalOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add document
                </Button>

                {/* Additional Prompt */}
                <div className="space-y-2">
                  <Label htmlFor="additionalPrompt">
                    Prompt agent when generating or rerunning the report (optional)
                  </Label>
                  <Textarea
                    id="additionalPrompt"
                    placeholder="Add specific instructions for the AI analysis..."
                    value={additionalPrompt}
                    onChange={(e) => setAdditionalPrompt(e.target.value)}
                    className="min-h-[100px] bg-white"
                  />
                </div>

                {/* Run Report Button */}
                <Button
                  type="button"
                  onClick={() => {
                    const prompt = additionalPrompt.trim() || undefined
                    toast.info(
                      prompt
                        ? `Sending report with prompt: "${prompt.slice(0, 50)}${prompt.length > 50 ? "..." : ""}"`
                        : "Sending report without additional prompt",
                    )
                    generateWillReportMutation.mutate({
                      estateId,
                      reportType: [ReportTypes.WILL_REVIEW],
                      additionalPrompt: prompt,
                    })
                  }}
                  className="w-full"
                  disabled={generateWillReportMutation.isPending}
                >
                  {generateWillReportMutation.isPending ? "Generating..." : "Run Report"}
                </Button>
                <Typography as="p" className="text-xs text-muted-foreground mt-3">
                  * Legacy Keeper reports use a complex agent orchestration to deliver personalised
                  insight for each client, saving hours of work. Due to the complex nature of the
                  report generation process you can expect each report to take between 10-20 minutes
                  as our agents evaluate the latest estate laws, user goals, and user data to
                  deliver a truly bespoke output for your clients and their families.
                </Typography>
              </div>
            </RefineReportsSection>
          }
          middleSection={<EstateRelationshipTree estateId={estateId} />}
        >
          {/* Report Insights Section - Conditional rendering based on report existence */}
          {willReports.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2 text-yellow-500" />
                  No Will Reports Generated
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Typography as="p" className="text-muted-foreground">
                  Generate a will report to view insights and recommendations for this estate's last
                  will and testament.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            (() => {
              const report = willReports[0]
              if (!report) return null

              return (
                <div className="mb-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg overflow-hidden">
                    <div className="p-6">
                      <CardTitle className="text-[#1e40af] text-xl font-bold mb-3 flex items-center">
                        <FileText className="h-5 w-5 mr-2" />
                        Last Will and Testament Insights
                      </CardTitle>

                      {report.summaryNotes ? (
                        <Typography as="p" className="text-gray-700 mb-4 font-medium">
                          {report.summaryNotes}
                        </Typography>
                      ) : (
                        <Typography as="p" className="text-gray-700 mb-4">
                          No summary notes available. Please read the full report.
                        </Typography>
                      )}

                      {report.content ? (
                        <Collapsible className="group">
                          <CollapsibleTrigger asChild>
                            <Button
                              variant="link"
                              className="p-0 h-auto text-blue-600 hover:text-blue-700 font-semibold"
                            >
                              <span className="group-data-[state=closed]:block group-data-[state=open]:hidden">
                                Read full summary
                              </span>
                              <span className="group-data-[state=closed]:hidden group-data-[state=open]:block">
                                Collapse summary
                              </span>
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-4">
                            <div className="prose max-w-none prose-slate">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {report.content}
                              </ReactMarkdown>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      ) : (
                        <Button
                          variant="link"
                          className="p-0 h-auto text-blue-600 hover:text-blue-700 font-semibold"
                        >
                          Read full summary
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })()
          )}
        </RecommendationsSection>
      </div>

      <UploadDocumentModal
        open={uploadModalOpen}
        setOpen={setUploadModalOpen}
        estateId={estateId}
        documentType={willDocumentType}
        onSuccess={() => {
          queryUtils.documents.listByType.invalidate()
        }}
      />
    </Page>
  )
}

// Powerpoint export
type WillReportForPpt = {
  summaryNotes?: string | null
  content?: string | null
}

type WillInfographicForPpt = {
  header?: {
    testatorName?: string
    reportDate?: string
  }
  recommendations?: Array<{
    order?: number
    action: string
    details?: string
    priority?: "HIGH" | "MEDIUM" | "LOW"
  }>
  gapsIdentified?: Array<{
    issue: string
    severity?: "HIGH" | "MEDIUM" | "LOW"
    details?: string
  }>
}

function extractRecommendationsFromMarkdown(content?: string | null) {
  if (!content) return []

  const recommendationSection = content.match(
    /(?:^|\n)#{1,3}\s*(?:key\s+)?(?:recommendations|recommended actions|action items|next steps)\s*\n([\s\S]*?)(?=\n#{1,3}\s|\s*$)/i,
  )

  const source = recommendationSection?.[1] ?? content

  const bullets = source
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => /^[-*•]\s+/.test(line))
    .map((line) =>
      line
        .replace(/^[-*•]\s+/, "")
        .replace(/\*\*/g, "")
        .trim(),
    )
    .filter(Boolean)

  if (bullets.length > 0) {
    return bullets.slice(0, 5).map((bullet) => ({
      title: bullet,
      details: undefined,
      priority: undefined,
    }))
  }

  return source
    .split(/\n{2,}/)
    .map((paragraph) =>
      paragraph
        .replace(/^#{1,3}\s*/, "")
        .replace(/\*\*/g, "")
        .trim(),
    )
    .filter((paragraph) => paragraph.length > 40)
    .slice(0, 5)
    .map((paragraph) => ({
      title: paragraph,
      details: undefined,
      priority: undefined,
    }))
}

function DownloadWillRecommendationsPowerPoint({
  report,
  infographicData,
}: {
  report: WillReportForPpt | null
  infographicData: WillInfographicForPpt | null | undefined
}) {
  const [isExporting, setIsExporting] = useState(false)

  function getRecommendations() {
    const structuredRecommendations = infographicData?.recommendations ?? []

    if (structuredRecommendations.length > 0) {
      return structuredRecommendations
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
        .slice(0, 5)
        .map((r) => ({
          title: r.action,
          details: r.details,
          priority: r.priority,
        }))
    }

    const severityRank = { HIGH: 1, MEDIUM: 2, LOW: 3 }

    const gapRecommendations = infographicData?.gapsIdentified ?? []

    if (gapRecommendations.length > 0) {
      return gapRecommendations
        .sort((a, b) => severityRank[a.severity ?? "LOW"] - severityRank[b.severity ?? "LOW"])
        .slice(0, 5)
        .map((g) => ({
          title: g.issue,
          details: g.details,
          priority: g.severity,
        }))
    }

    if (report?.summaryNotes) {
      return [
        {
          title: report.summaryNotes,
          details: undefined,
          priority: undefined,
        },
      ]
    }

    const contentFallback = extractRecommendationsFromMarkdown(report?.content)

    if (contentFallback.length > 0) {
      return contentFallback
    }

    return []
  }

  async function handleDownload() {
    if (!report) {
      toast.error("No will report available to export")
      return
    }

    setIsExporting(true)

    try {
      const recommendations = getRecommendations()

      if (recommendations.length === 0) {
        toast.error("No recommendations available to export")
        return
      }

      const pptx = new PptxGenJS()
      pptx.layout = "LAYOUT_WIDE"
      pptx.author = "LegacyKeeper"
      pptx.title = "Last Will & Testament Recommendations"

      const slide = pptx.addSlide()

      const W = 13.333
      const H = 7.5
      const PAD = 0.6

      const testatorName = infographicData?.header?.testatorName ?? "Estate"
      const dateLabel = new Date().toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      })

      slide.addText("Last Will & Testament", {
        x: PAD,
        y: 0.35,
        w: W - PAD * 2,
        h: 0.3,
        fontFace: "Calibri",
        fontSize: 13,
        bold: true,
        color: "2B6CB0",
      })

      slide.addText("Key Recommendations", {
        x: PAD,
        y: 0.72,
        w: W - PAD * 2,
        h: 0.45,
        fontFace: "Calibri",
        fontSize: 26,
        bold: true,
        color: "111827",
      })

      slide.addText(`${testatorName} • ${dateLabel}`, {
        x: PAD,
        y: 1.18,
        w: W - PAD * 2,
        h: 0.3,
        fontFace: "Calibri",
        fontSize: 11,
        color: "6B7280",
      })

      slide.addShape(pptx.ShapeType.roundRect, {
        x: PAD,
        y: 1.65,
        w: W - PAD * 2,
        h: H - 2.25,
        fill: { color: "FFFFFF" },
        line: { color: "E5E7EB", width: 1 },
        rectRadius: 0.12,
      })

      recommendations.forEach((item, index) => {
        const y = 2.2 + index * 0.95

        slide.addShape("ellipse", {
          x: PAD + 0.35,
          y: y + 0.05,
          w: 0.32,
          h: 0.32,
          fill: { color: "DBEAFE" },
          line: { color: "2B6CB0", width: 1 },
        })

        slide.addText(String(index + 1), {
          x: PAD + 0.35,
          y: y + 0.085,
          w: 0.32,
          h: 0.24,
          fontFace: "Calibri",
          fontSize: 10,
          color: "2B6CB0",
          align: "center",
          valign: "middle",
        })

        slide.addText(item.title, {
          x: PAD + 0.85,
          y,
          w: 10.8,
          h: 0.3,
          fontFace: "Calibri",
          fontSize: 14,
          bold: true,
          color: "111827",
          fit: "shrink",
        })

        if (item.details) {
          slide.addText(item.details, {
            x: PAD + 0.85,
            y: y + 0.32,
            w: 10.8,
            h: 0.38,
            fontFace: "Calibri",
            fontSize: 10.5,
            color: "4B5563",
            fit: "shrink",
          })
        }

        if (item.priority) {
          slide.addText(item.priority, {
            x: W - 1.8,
            y,
            w: 0.9,
            h: 0.25,
            fontFace: "Calibri",
            fontSize: 9,
            bold: true,
            color: item.priority === "HIGH" ? "DC2626" : "6B7280",
            align: "right",
          })
        }
      })

      slide.addText("LegacyKeeper", {
        x: PAD,
        y: H - 0.45,
        w: 3,
        h: 0.25,
        fontFace: "Calibri",
        fontSize: 10,
        color: "6B7280",
      })

      const safeName = testatorName.replace(/[^\w\s-]/g, "").replace(/\s+/g, "_")
      await pptx.writeFile({
        fileName: `Will_Recommendations_${safeName}.pptx`,
      })

      toast.success("PowerPoint downloaded")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "PowerPoint export failed")
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleDownload}
      disabled={!report || isExporting}
    >
      <Download className="h-4 w-4 mr-1" />
      {isExporting ? "Preparing PowerPoint..." : "Download recommendations PPT"}
    </Button>
  )
}

type WillInfographicBeneficiaryTarget =
  | string
  | {
      name: string
      initials?: string
      targetType: "contact" | "company" | "trust" | "estate"
      sharePercentage?: number
      beneficiaryClass?: string
      distributionMethod?: string
      conditions?: string
    }

type WillInfographicForExcel = WillInfographicForPpt & {
  beneficiaries?: Array<{
    name: string
    relationship?: string
    sharePercentage?: number
    status?: "ADULT" | "MINOR" | "UNKNOWN"
    age?: number
  }>
  estateAssets?: Array<{
    name: string
    assetType?: string
    ownership?: string
    value?: number
    bequestRecipient?: string
    conditions?: string
  }>
  keyStakeholders?: Array<{
    role: string
    roleLabel?: string
    name?: string
    position?: string
    isMissing?: boolean
  }>
  trusts?: Array<{
    name: string
    trustType?: string
    trustTypeLabel?: string
    trustee?: string
    successorTrustee?: string
    beneficiaries?: WillInfographicBeneficiaryTarget[]
    distribution?: string
    distributionAge?: number
    conditions?: string[]
  }>
}

function DownloadWillDataExcel({
  infographicData,
}: {
  infographicData: WillInfographicForExcel | null | undefined
}) {
  function handleDownload() {
    if (!infographicData?.header) {
      toast.error("No will report data available to export")
      return
    }

    const workbook = XLSX.utils.book_new()

    const assets = infographicData.estateAssets ?? []
    const beneficiaries = infographicData.beneficiaries ?? []
    const stakeholders = infographicData.keyStakeholders ?? []
    const trusts = infographicData.trusts ?? []
    const recommendations = infographicData.recommendations ?? []
    const gaps = infographicData.gapsIdentified ?? []

    const summaryRows = [
      ["Testator", infographicData.header.testatorName ?? ""],
      ["Report date", infographicData.header.reportDate ?? ""],
      ["Assets", assets.length],
      ["Beneficiaries", beneficiaries.length],
      ["Trusts", trusts.length],
      ["Stakeholders", stakeholders.length],
      ["Recommendations", recommendations.length],
      ["Gaps identified", gaps.length],
    ]

    const summarySheet = XLSX.utils.aoa_to_sheet([["Field", "Value"], ...summaryRows])
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary")

    const assetsSheet = XLSX.utils.json_to_sheet(
      assets.map((asset) => ({
        Name: asset.name,
        Type: asset.assetType ?? "",
        Ownership: asset.ownership ?? "",
        Value: asset.value ?? "",
        "Bequest recipient": asset.bequestRecipient ?? "",
        Conditions: asset.conditions ?? "",
      })),
    )
    XLSX.utils.book_append_sheet(workbook, assetsSheet, "Assets")

    const beneficiariesSheet = XLSX.utils.json_to_sheet(
      beneficiaries.map((beneficiary) => ({
        Name: beneficiary.name,
        Relationship: beneficiary.relationship ?? "",
        "Share %": beneficiary.sharePercentage ?? "",
        Status: beneficiary.status ?? "",
        Age: beneficiary.age ?? "",
      })),
    )
    XLSX.utils.book_append_sheet(workbook, beneficiariesSheet, "Beneficiaries")

    const stakeholdersSheet = XLSX.utils.json_to_sheet(
      stakeholders.map((stakeholder) => ({
        Role: stakeholder.roleLabel ?? stakeholder.role,
        Name: stakeholder.name ?? "",
        Position: stakeholder.position ?? "",
        Status: stakeholder.isMissing ? "Missing" : "Assigned",
      })),
    )
    XLSX.utils.book_append_sheet(workbook, stakeholdersSheet, "Key Roles")

    const trustsSheet = XLSX.utils.json_to_sheet(
      trusts.map((trust) => ({
        Name: trust.name,
        Type: trust.trustTypeLabel ?? trust.trustType ?? "",
        Trustee: trust.trustee ?? "",
        "Successor trustee": trust.successorTrustee ?? "",
        Beneficiaries:
          trust.beneficiaries
            ?.map((beneficiary) =>
              typeof beneficiary === "string" ? beneficiary : beneficiary.name,
            )
            .join(", ") ?? "",
        Distribution: trust.distribution ?? "",
        Conditions: trust.conditions?.join(", ") ?? "",
      })),
    )
    XLSX.utils.book_append_sheet(workbook, trustsSheet, "Trusts")

    const recommendationsSheet = XLSX.utils.json_to_sheet(
      recommendations.map((recommendation) => ({
        Order: recommendation.order ?? "",
        Priority: recommendation.priority ?? "",
        Action: recommendation.action,
        Details: recommendation.details ?? "",
      })),
    )
    XLSX.utils.book_append_sheet(workbook, recommendationsSheet, "Recommendations")

    const gapsSheet = XLSX.utils.json_to_sheet(
      gaps.map((gap) => ({
        Severity: gap.severity ?? "",
        Issue: gap.issue,
        Details: gap.details ?? "",
      })),
    )
    XLSX.utils.book_append_sheet(workbook, gapsSheet, "Gaps")

    for (const sheetName of workbook.SheetNames) {
      const sheet = workbook.Sheets[sheetName]
      if (!sheet?.["!ref"]) continue

      const range = XLSX.utils.decode_range(sheet["!ref"])

      sheet["!cols"] = Array.from({ length: range.e.c + 1 }).map((_, colIndex) => {
        let maxLength = 12

        for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++) {
          const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })
          const cell = sheet[cellAddress]
          const value = cell?.v ? String(cell.v) : ""
          maxLength = Math.max(maxLength, Math.min(value.length + 2, 42))
        }

        return { wch: maxLength }
      })
    }

    const testatorName = infographicData.header.testatorName ?? "Estate"
    const safeName = testatorName.replace(/[^\w\s-]/g, "").replace(/\s+/g, "_")

    XLSX.writeFile(workbook, `Will_Report_Data_${safeName}.xlsx`)
    toast.success("Excel downloaded")
  }

  return (
    <Button type="button" variant="outline" onClick={handleDownload} disabled={!infographicData}>
      <Download className="h-4 w-4 mr-1" />
      Download Excel
    </Button>
  )
}
